import {HeartFillIcon, HeartIcon, LinkIcon} from '@primer/octicons-react'
import {SkeletonText} from '@primer/react/experimental'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {SidebarSponsorsProps} from '../types'
import {SidebarSection} from './SidebarSection'
import {SidebarLink} from './SidebarLink'
import styles from './SidebarSponsors.module.css'

/** Renders the Sponsors section of the repository sidebar. */
export function SidebarSponsors({
  sponsorables,
  externalFundingLinks,
  sponsorsPath,
  sponsorsLearnMorePath,
  sponsorableCount,
}: SidebarSponsorsProps) {
  const learnMorePath = sponsorsLearnMorePath ?? sponsorsPath
  const showLearnMore = (sponsorableCount ?? sponsorables.length) > 0
  return (
    <SidebarSection title="Sponsor this project">
      {sponsorables.length > 0 && (
        <ul className={styles.sponsorList}>
          {sponsorables.map(user => (
            <li key={`sponsor-${user.login}`} className={styles.sponsorItem}>
              <SidebarLink to={user.profilePath} analyticsLabel="section:sponsors">
                <GitHubAvatar src={user.avatarUrl} alt={`@${user.login}`} size={32} />
              </SidebarLink>
              <span className={styles.sponsorName}>
                <SidebarLink to={user.profilePath} className={styles.nameLink} analyticsLabel="section:sponsors">
                  <strong>{user.login}</strong>
                </SidebarLink>
                {user.displayName && <span className={styles.displayName}>{user.displayName}</span>}
              </span>
              <SidebarLink
                to={`/sponsors/${user.login}`}
                className={styles.sponsorButton}
                analyticsLabel={
                  user.isSponsoredByViewer ? 'section:sponsors;action:sponsoring' : 'section:sponsors;action:sponsor'
                }
              >
                {user.isSponsoredByViewer ? (
                  <HeartFillIcon size={16} className={styles.heartSponsoring} />
                ) : (
                  <HeartIcon size={16} className={styles.heartSponsor} />
                )}
                <span className="sr-only">
                  {`${user.isSponsoredByViewer ? 'Sponsoring' : 'Sponsor'} @${user.login}`}
                </span>
              </SidebarLink>
            </li>
          ))}
        </ul>
      )}

      {externalFundingLinks.length > 0 && (
        <ul className={styles.sponsorList}>
          {externalFundingLinks.map(link => (
            <li key={`funding-${link.platform}-${link.url}`} className={styles.sponsorItem}>
              <span className={styles.fundingIcon}>
                {link.iconUrl ? (
                  <img
                    src={link.iconUrl}
                    width={32}
                    height={32}
                    alt=""
                    aria-hidden="true"
                    className={styles.platformIcon}
                  />
                ) : (
                  <LinkIcon size={16} />
                )}
              </span>
              <span className={styles.fundingName}>
                <SidebarLink
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  analyticsLabel="section:sponsors"
                >
                  {link.label ?? link.platform}
                </SidebarLink>
              </span>
            </li>
          ))}
        </ul>
      )}

      {showLearnMore && (
        <div className={styles.learnMore}>
          <SidebarLink to={learnMorePath} inline>
            <span>Learn more about GitHub Sponsors</span>
          </SidebarLink>
        </div>
      )}
    </SidebarSection>
  )
}

export function SidebarSponsorsSkeleton() {
  return (
    <SidebarSection title="Sponsor this project">
      <SkeletonText />
    </SidebarSection>
  )
}
